import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { formatPercentage } from '~utils/utilFunctions';

import { formatAccountingAmount, getLineItemTypeLabel } from './settlementSheetDisplay';
import { isPercentageAddOnCharge, SettlementSheetCharge } from './settlementSheetLines';

const describeAddOnCharge = (addOnCharge: AddOnCharge) =>
  isPercentageAddOnCharge(addOnCharge)
    ? [addOnCharge.name, formatPercentage(addOnCharge.percentage)].join(' ')
    : addOnCharge.name;

const removedLineSx = (theme: Theme) => ({
  borderTop: `1px solid ${theme.semantic.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  px: 2,
  py: 1,
});

interface RemoveChargeDialogProps {
  charge: SettlementSheetCharge | null;
  chargeTotal: number;
  currencyFormatter: (value: number) => string;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: () => void;
  settlementName: string;
  voidsSettlement: boolean;
}

export default function RemoveChargeDialog({
  charge,
  chargeTotal,
  currencyFormatter,
  isBusy,
  onClose,
  onConfirm,
  settlementName,
  voidsSettlement,
}: RemoveChargeDialogProps) {
  const addOnChargeNames = charge?.addOnCharges.map(describeAddOnCharge) ?? [];

  return (
    <ConfirmationDialog
      confirmButtonProps={{
        children: t('approvals.driver_pay.settlements.sheet.remove_charge.confirm'),
      }}
      isBusy={isBusy}
      isOpen={charge !== null}
      onClose={onClose}
      onConfirm={onConfirm}
      title={t('approvals.driver_pay.settlements.sheet.remove_charge.title', {
        settlementId: settlementName,
      })}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('approvals.driver_pay.settlements.sheet.remove_charge.description')}
      </Typography>
      {voidsSettlement && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('approvals.driver_pay.settlements.sheet.remove_charge.voids_settlement', {
            settlementId: settlementName,
          })}
        </Alert>
      )}
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.semantic.border}`,
          borderRadius: 1,
        })}
      >
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', display: 'block', px: 2, py: 0.5 }}
        >
          {t('approvals.driver_pay.settlements.sheet.remove_charge.removed_together')}
        </Typography>
        {charge?.lineItems.map((lineItem) => (
          <Typography key={lineItem.id} variant="body2" sx={removedLineSx}>
            {getLineItemTypeLabel(lineItem)}
          </Typography>
        ))}
        {addOnChargeNames.length > 0 && (
          <Typography variant="body2" sx={removedLineSx}>
            {t('approvals.driver_pay.settlements.sheet.remove_charge.add_ons', {
              count: addOnChargeNames.length,
              names: addOnChargeNames.join(', '),
            })}
          </Typography>
        )}
        <Box sx={removedLineSx}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('approvals.driver_pay.settlements.sheet.remove_charge.charge_total')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatAccountingAmount(chargeTotal, currencyFormatter)}
          </Typography>
        </Box>
      </Box>
    </ConfirmationDialog>
  );
}
